import TnAvatar from '@components/tn-avatar/TnAvatar';
import TnButton from '@components/tn-button/TnButton';
import TnTypography from '@components/tn-typography/TnTypography';
import { createStyles, lighten, makeStyles } from '@material-ui/core';
import { Cached, DeleteOutline } from '@material-ui/icons';
import axios from 'axios';
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ReactComponent as DocIcon } from '../../static/_assets/docIcon.svg';
import { ReactComponent as FileUploadSmall } from '../../static/_assets/file-upload-small.svg';
import { ReactComponent as FileUpload } from '../../static/_assets/file-upload.svg';
import { ReactComponent as PDFIcon } from '../../static/_assets/pdfIcon.svg';
import { ReactComponent as FileIcon } from '../../static/_assets/report_file.svg';
import { ReactComponent as XlsIcon } from '../../static/_assets/XlsIcon.svg';

export interface DocumentData {
  id: string;
  doc_category: string;
  docType: string;
  file_name: string;
  isMulti: boolean;
  files: FileData[];
}

export interface FileData {
  fileName: string;
  url: string;
  fileSize: string;
}

interface Props {
  addFile: (id: string, fileName: string, url: string, fileIndex: number, fileSize: string) => void;
  containerName?: string;
  deleteFile: (id: string, fileIndex: number) => void;
  documentData: DocumentData;
  onUploadError: () => void;
  isEdit?: boolean;
  readOnly?: boolean;
  parseResume?: (resumeUrl: string) => void;
  resumeParseComplete?: boolean;
  resumeParseError?: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    sectionContainer: {
      width: 'calc(100% - 32px)',
      padding: '16px',
      border: '1px solid #E0E0E0',
      borderRadius: 4,
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gridGap: 8,
    },
    uploadedFileContainer: {
      width: 'calc(100% - 32px)',
      backgroundColor: '#FAFAFA',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      border: '1px solid #E0E0E0',
      padding: '16px',
      gridGap: 8,
      borderRadius: 4,
      justifyContent: 'flex-start',
      '&:not(:last-child)': {
        borderBottom: '1px solid #E0E0E0',
      },
    },
    fileContainer: {
      width: '100%',
      justifyContent: 'space-between',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      '&:not(:last-child)': {
        borderBottom: '1px solid #E0E0E0',
        paddingBottom: 8,
      },
    },
    documentElement: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gridGap: 8,
    },
    fileNameContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    afterFileUploadContainer: {
      width: 'calc(100% - 32px)',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      border: '1px solid #E0E0E0',
      padding: '16px',
      gridGap: 8,
      borderRadius: 4,
    },
    resumeContainer: {
      width: '100%',
      padding: '16px',
      border: '1px solid #E0E0E0',
      borderRadius: 4,
      backgroundColor: '#FAFAFA',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gridGap: 8,
    },
    resumeLabelContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gridGap: 16,
    },
    deleteIconContainer: {
      cursor: 'pointer',
      border: `1px solid ${lighten('#B00020', 0.5)}`,
      padding: '4px 4px 0px 4px',
      borderRadius: 4,
    },
    deleteIcon: {
      fill: '#B00020',
    },
    fileTypeIconContainer: {
      padding: '8px 8px 4px 8px',
      border: '1px solid #E0E0E0',
      borderRadius: 4,
    },
    link: {
      textDecoration: 'none',
    },
    avatar: {
      margin: 0,
    },
    uploadButton: {
      fontFamily: 'Avenir Heavy, Arial, Verdana, sans-serif',
      fontWeight: 800,
    },
    documentLabel: {
      alignItems: 'baseline',
    },
    uploadAction: {
      cursor: 'pointer',
    },
    hiddenInput: {
      display: 'none',
    },
    flexContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    loadingSpinner: {
      animation: '$spin 1.5s linear infinite',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
    parseErrorText: {
      color: '#B00020',
      marginTop: 4,
    },
  }),
);

export default function MultiFileUploader({
  addFile,
  containerName = 'candidate',
  deleteFile,
  documentData,
  onUploadError,
  isEdit = false,
  readOnly = false,
  parseResume,
  resumeParseComplete = false,
  resumeParseError = false,
}: Readonly<Props>) {
  const classes = useStyles();
  const inputRef: React.MutableRefObject<HTMLInputElement | undefined> = useRef();
  const [fileName, setFileName] = useState('');

  const fileList = documentData.files;

  const prepareFile = () => {
    if (inputRef.current?.files && inputRef.current?.files?.length > 0) {
      const files = inputRef.current.files;
      const fileType: Array<string> = files[0]?.name?.split('.');
      const type: string = fileType ? fileType[fileType?.length - 1] : '';

      if (!documentData.file_name || documentData.file_name.includes(type)) {
        setFileName(files[0]?.name);
        const sizeInKB = (files[0]?.size ?? 0) / 1024;
        const sizeInMB = (files[0]?.size ?? 0) / (1024 * 1024);
        let sizeText = '';
        if (sizeInKB < 1024) {
          sizeText = `${sizeInKB.toFixed(2)} KB`;
        } else {
          sizeText = `${sizeInMB.toFixed(2)} MB`;
        }
        uploadFile(files, sizeText);
      }
    }
  };

  const uploadFile = (files: FileList, sizeText: string) => {
    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    };

    const data = new FormData();

    data.append('file', files[0]);
    data.append('container_name', containerName);
    data.append('nested_container_name', '');

    axios
      .post(`${process.env.UPLOAD_URI}/upload-file`, data, config)
      .then((response) => {
        const fileUrl = response.data.result ? response.data.result.urls[1] : response.data;
        setFileName(fileUrl);
        addFile(documentData.id, files[0].name, fileUrl, fileList?.length ?? 0, sizeText);
        if (documentData.doc_category === 'Resume' && response.data.result?.urls?.[0]) {
          parseResume?.(response.data.result.urls[0]);
        }
      })
      .catch(() => {
        onUploadError?.();
      });
  };

  const deleteDocument = (fileIndex: number) => {
    if (isEdit) {
      deleteFile(documentData.id, fileIndex);
    } else {
      axios
        .post(`${process.env.UPLOAD_URI}/delete-file`, {
          container_name: containerName,
          file_name: fileName,
        })
        .then(() => {
          deleteFile(documentData.id, fileIndex);
        });
    }
  };

  const getDeleteIcon = (index: number) => (
    <div aria-hidden="true" className={classes.documentElement} onClick={() => deleteDocument(index)}>
      <div className={classes.deleteIconContainer}>
        <DeleteOutline className={classes.deleteIcon} />
      </div>
    </div>
  );

  const getFileExtensionIcon = (name: string, url: string) => {
    const fileType: Array<string> = name?.split('.');
    const type: string = fileType ? fileType[fileType?.length - 1] : '';

    if (['jpg', 'jpeg', 'png'].includes(type.toLowerCase())) {
      return <TnAvatar src={url} name={name} isCompletePath className={classes.avatar} />;
    }

    let fileIcon: JSX.Element;
    switch (type.toLowerCase()) {
      case 'pdf':
        fileIcon = <PDFIcon />;
        break;
      case 'doc':
      case 'docx':
        fileIcon = <DocIcon />;
        break;
      case 'xls':
      case 'xlsx':
        fileIcon = <XlsIcon />;
        break;
      default:
        fileIcon = <FileIcon />;
        break;
    }
    return <div className={classes.fileTypeIconContainer}>{fileIcon}</div>;
  };

  const renderFile = (file: FileData, index: number) => {
    return (
      <div className={classes.fileContainer} key={file.fileName}>
        <div className={classes.documentElement}>
          {getFileExtensionIcon(file.fileName, file.url)}
          <div className={classes.fileNameContainer}>
            <Link to={{ pathname: file.url }} target="_blank" className={classes.link}>
              <TnTypography variant="caption2" color="blue">
                {file.fileName}
              </TnTypography>
            </Link>
            {!!file.fileSize && (
              <TnTypography variant="caption2" color="lightGrey">
                {file.fileSize}
              </TnTypography>
            )}
          </div>
        </div>
        {!readOnly && getDeleteIcon(index)}
      </div>
    );
  };

  // Resume section
  if (readOnly) {
    return (
      <div className={classes.uploadedFileContainer}>
        {fileList?.map((file: FileData, index: number) => renderFile(file, index))}
      </div>
    );
  }

  if (documentData.doc_category === 'Resume') {
    if (fileList && fileList.length > 0) {
      const resume = fileList[0];

      if (resumeParseComplete) {
        return (
          <>
            <div className={classes.uploadedFileContainer}>
              <div className={classes.fileContainer}>
                <div className={classes.documentElement}>
                  {getFileExtensionIcon(resume.fileName, resume.url)}
                  <div className={classes.fileNameContainer}>
                    <Link to={{ pathname: resume.url }} target="_blank" className={classes.link}>
                      <TnTypography variant="caption2" color="blue">
                        {resume.fileName}
                      </TnTypography>
                    </Link>
                    <TnTypography variant="caption2" color="lightGrey">
                      {resume.fileSize}
                    </TnTypography>
                  </div>
                </div>
                {getDeleteIcon(0)}
              </div>
            </div>
            {resumeParseError && (
              <TnTypography variant="caption2" className={classes.parseErrorText}>
                Resume parsing failed. Try uploading another resume or manually fill in the candidate details.
              </TnTypography>
            )}
          </>
        );
      }

      return (
        <div className={classes.resumeContainer} style={{ height: 134 }}>
          <div className={classes.loadingSpinner}>
            <Cached style={{ display: 'block' }} />
          </div>
          <TnTypography variant="body2">Processing...</TnTypography>
        </div>
      );
    }

    return (
      <div className={classes.resumeContainer}>
        <FileUpload />
        <div className={classes.resumeLabelContainer}>
          <TnTypography bold variant="body2">
            Upload the Candidate Resume
          </TnTypography>
          <TnTypography variant="body2" color="orange">
            Required
          </TnTypography>
        </div>
        <TnTypography variant="caption2" color="lightGrey">
          Maximum 3MB. Supports: {documentData?.file_name}
        </TnTypography>
        <TnButton
          value="Upload"
          color="primary"
          variant="contained"
          onClick={() => inputRef.current?.click()}
          className={classes.uploadButton}
        />
        <input
          type={'file'}
          className={classes.hiddenInput}
          ref={inputRef as React.RefObject<HTMLInputElement>}
          id={documentData.doc_category}
          onChange={() => prepareFile()}
          accept={documentData.file_name}
        />
      </div>
    );
  }

  // Other documents - no file uploaded yet
  if (!fileList || fileList.length === 0) {
    return (
      <div className={classes.sectionContainer}>
        <div className={`${classes.documentElement} ${classes.documentLabel}`}>
          <TnTypography variant="body2" bold>
            {documentData.doc_category}
          </TnTypography>
          <TnTypography variant="caption2" color="lightGrey">
            Maximum 3MB. Supports: {documentData.file_name}
          </TnTypography>
        </div>
        <div
          aria-hidden="true"
          className={`${classes.documentElement} ${classes.uploadAction}`}
          onClick={() => inputRef.current?.click()}
        >
          <FileUploadSmall color="#4471B8" />
          <TnTypography variant="caption2" bold color="blue">
            Upload
          </TnTypography>
          <input
            type={'file'}
            className={classes.hiddenInput}
            ref={inputRef as React.RefObject<HTMLInputElement>}
            id={documentData.doc_category}
            onChange={() => prepareFile()}
            accept={documentData.file_name}
          />
        </div>
      </div>
    );
  }

  // Other documents - files uploaded
  return (
    <div className={classes.afterFileUploadContainer}>
      <div className={classes.flexContainer}>
        <div className={`${classes.documentElement} ${classes.documentLabel}`}>
          <TnTypography variant="body2" bold>
            {documentData.doc_category}
          </TnTypography>
          <TnTypography variant="caption2" color="lightGrey">
            Maximum 3MB. Supports: {documentData.file_name}
          </TnTypography>
        </div>
        {documentData.isMulti && (
          <div
            aria-hidden="true"
            className={`${classes.documentElement} ${classes.uploadAction}`}
            onClick={() => inputRef.current?.click()}
          >
            <FileUploadSmall color="#4471B8" />
            <TnTypography variant="caption2" bold color="blue">
              Upload {documentData.doc_category}
            </TnTypography>
            <input
              type={'file'}
              className={classes.hiddenInput}
              ref={inputRef as React.RefObject<HTMLInputElement>}
              id={documentData.doc_category}
              onChange={() => prepareFile()}
              accept={documentData.file_name}
            />
          </div>
        )}
      </div>
      <div className={classes.uploadedFileContainer}>
        {fileList.map((file: FileData, index: number) => renderFile(file, index))}
      </div>
    </div>
  );
}
